/**
 * The audit log's date range, as ONE control.
 *
 * It was two bare `<input type="date">`s side by side, which made the single
 * most-used filter on the screen the widest and the slowest: picking "the last
 * week" meant two calendar pops and a subtraction the reader did in their
 * head.
 *
 * So the resting state is one button reading the range back in words, and the
 * panel behind it leads with the three windows this screen is actually opened
 * for (7 / 15 / 40 days). The exact from/to inputs are still there, below the
 * presets and clearly secondary -- an investigation pinned to a known date
 * still needs them, and nothing has been taken away.
 *
 * The presets are RELATIVE and recomputed on open, so a tab left overnight
 * offers "last 7 days" from today, not from yesterday.
 */

import { useEffect, useRef, useState } from 'react';
import { CalendarRange, Check, ChevronDown } from 'lucide-react';

import {
  activePreset,
  presetRange,
  RANGE_PRESET_DAYS,
  type RangePresetDays,
} from './auditFilters';
import { formatLocalShort, startOfLocalDay, type LocalDay } from './auditTime';

interface Props {
  fromDay: LocalDay;
  /** INCLUSIVE, as the reader thinks of it. */
  toDay: LocalDay;
  onChange: (range: { fromDay: LocalDay; toDay: LocalDay }) => void;
}

/** `13 Sep 2026` -- the range summary's date format, with no time of day. */
function formatDay(day: LocalDay): string {
  return formatLocalShort(startOfLocalDay(day).toISOString()).replace(/,?\s*\d{2}:\d{2}$/, '');
}

export function DateRangeField({ fromDay, toDay, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const preset = activePreset({ fromDay, toDay });

  // Dismiss on an outside click or Escape, like the actor picker beside it.
  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const summary =
    preset !== null ? `Last ${preset} days` : `${formatDay(fromDay)} – ${formatDay(toDay)}`;

  const choosePreset = (days: RangePresetDays) => {
    onChange(presetRange(days));
    setOpen(false);
  };

  return (
    <div className="range-picker" ref={boxRef}>
      <button
        type="button"
        className="range-trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Date range: ${summary}`}
        onClick={() => setOpen((current) => !current)}
      >
        <CalendarRange aria-hidden="true" />
        <span className="range-trigger-label">{summary}</span>
        <ChevronDown aria-hidden="true" />
      </button>

      {open && (
        <div className="range-panel" role="dialog" aria-label="Choose a date range">
          <div className="range-presets">
            {RANGE_PRESET_DAYS.map((days) => (
              <button
                key={days}
                type="button"
                className={preset === days ? 'range-preset is-chosen' : 'range-preset'}
                aria-pressed={preset === days}
                onClick={() => choosePreset(days)}
              >
                <span>Last {days} days</span>
                {preset === days && <Check aria-hidden="true" />}
              </button>
            ))}
          </div>

          <div className="range-custom">
            <span className="hint range-custom-title">Or pick exact dates</span>
            <div className="range-inputs">
              <label className="range-input">
                <span className="hint">From</span>
                <input
                  type="date"
                  aria-label="From date"
                  value={fromDay}
                  max={toDay}
                  required
                  // An emptied date is ignored rather than sent: the screen
                  // always queries within a range, which is what keeps the
                  // unindexed outcome and family filters cheap.
                  onChange={(event) =>
                    event.target.value && onChange({ fromDay: event.target.value, toDay })
                  }
                />
              </label>
              <label className="range-input">
                <span className="hint">To</span>
                <input
                  type="date"
                  aria-label="To date (inclusive)"
                  value={toDay}
                  min={fromDay}
                  required
                  onChange={(event) =>
                    event.target.value && onChange({ fromDay, toDay: event.target.value })
                  }
                />
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
